import { useCallback } from 'react';
import BaseTimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';

import { useRecoilValue } from 'recoil';

import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { useWorkingHours } from 'lib/hooks/useWorkingHours';

import { currentPractice } from 'components/practice/practiceState';
import 'components/shared/DateInputPicker/lib/DateInputPicker.scss';

import Popover from '../Popovers/Popover/Popover';
import ClearIcon from '../shared/ClearIcon';
import Header from '../shared/Header';

import RightComponent from './components/RightComponent';
import { getTimezoneLabel } from './lib/getTimezoneLabel';

/**
 * @param {import('./lib/propTypes').TimePickerProps} props
 */

const TimePicker = (props) => {
  const {
    value,
    onChange = () => {},
    disabled = false,
    className,
    inputClassName,
    panelClassName,
    parentClassName,
    buttonClassName,
    hasOptions = true,
    options = [],
    optionPosition = 'left',
    isClearable = false,
    stepMinutes = 5,
    icon = 'clock-mono',
    label = '',
    required = false,
    error = null,
    isTimezone = true,
    showRightComponent = true
  } = props;

  const currPractice = useRecoilValue(currentPractice);
  const userTimezone = useTimezone();
  const workingHours = useWorkingHours(currPractice, stepMinutes);

  const optionsList = hasOptions
    ? ia(options)
      ? options
      : workingHours.map((item) => ({ ...item, onClick: () => onChange(item.value) }))
    : [];

  const hasValidOptions = hasOptions && ia(optionsList);
  const isValue = Boolean(value);
  const resolvedIcon = icon === true ? 'clock-mono' : icon;
  const shouldShowIcon = Boolean(resolvedIcon);
  const timezone = getTimezoneLabel(value, isTimezone, userTimezone);

  const handleClear = useCallback(() => onChange(null), [onChange]);

  return (
    <div className={cn('w-full', parentClassName)}>
      <Header label={label} required={required} />
      <Popover
        disabled={disabled}
        className={cn(
          'custom-picker relative w-full min-w-[140px] rounded-md border border-solid border-neutral-200 bg-white px-3 !pr-0 focus-within:border-primary-300 focus-within:!outline focus-within:outline-2 focus-within:outline-[#CBF0FD]',
          className
        )}
        innerWrapperClassName="!static"
        panelClassName={cn(
          'max-h-[350px] overflow-y-auto w-full',
          !hasValidOptions && 'hidden',
          panelClassName
        )}
        position={optionPosition}
        text={value}
        customButton={
          <BaseTimePicker
            className={cn(isValue && 'filled', hasValidOptions && 'is-options', inputClassName)}
            disabled={disabled}
            format="hh:mm a"
            hourPlaceholder="HH"
            minutePlaceholder="mm"
            isOpen={false}
            disableClock
            clockIcon={null}
            clearIcon={
              <ClearIcon isClearable={isClearable} onClick={handleClear} isValue={isValue} />
            }
            value={value}
            onChange={onChange}
          />
        }
        customButtonClassName={cn(
          'w-full justify-items-end h-10 pr-3',
          hasValidOptions ? 'cursor-pointer' : 'cursor-default',
          buttonClassName
        )}
        customButtonTrigger={
          showRightComponent ? (
            <RightComponent
              timezone={timezone}
              icon={resolvedIcon}
              shouldShowIcon={shouldShowIcon}
            />
          ) : null
        }
        options={optionsList}
      />
      {error && <p className="pt-2 text-sm text-danger-500">{error}</p>}
    </div>
  );
};

export default TimePicker;
